import { isPending } from "@/common/common";
import { AddNameAndEmailForm, RadioFieldQuestion, SelectField } from "@/common/commonComponents";
import { DecisionType } from "@/common/decisions";
import { Alert } from "@/components-v2/Alert";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { Box, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Caption } from "@/components-v2/Caption";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { MDateInput } from "@/components-v2/DateInput";
import { Icon } from "@/components-v2/Icon";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MTextInput, TextInput } from "@/components-v2/TextInput";
import {
  prepDocumentAttachmentsPanel,
  prepUploadDocumentAttachmentsButton,
} from "@/dbot/attachments";
import { AgendaActions } from "@/dbot/common/commonAgendaComponents";
import {
  CommonBoardMeetingCustomPanel,
  getCompanyRoleLabelById,
  getNoticeToGmPanels,
} from "@/dbot/common/commonBoardMeetingDomain.ts";
import {
  CollapseList,
  CollapsibleCheckboxGroup,
  DecisionTextsView,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentSigningStatusLine,
  LocalDateTimePicker,
  ReopenDocumentButton,
} from "@/dbot/common/commonComponents";
import { DecisionItem, DetailsPanel, getDecisionDetailsPanels } from "@/dbot/common/commonDomain";
import { BoardMeetingForm } from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import { relativeTime } from "@/i18n/dateFormat";
import { useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { Markdown } from "@/text/Markdown.tsx";
import { isProductionEnvironment } from "@/utils/environments";
import { when } from "@/utils/functions";
import { partition } from "lodash";
import { observer } from "mobx-react";
import { FC, ReactNode } from "react";
import { NoticeToGmPanel } from "../common/commonBoardMeetingComponents";
import {
  BoardMinutesCustomPanel,
  getCustomBoardMeetingPanels,
  SignersMode,
  supportsSelectionOfSigners,
} from "./boardMeetingMinutesDomain";
import { BoardMeetingMinutesForm, VotingModel } from "./BoardMeetingMinutesForm";

const ExtraParticipants: FC<{ form: BoardMeetingMinutesForm }> = observer(({ form }) => {
  const { t } = useTranslation();

  return (
    <VBox>
      <Box>
        <div>{t("documents.boardMeeting.whoAttendedExtraParticipants")}</div>
      </Box>
      {form.extraParticipants.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"} justifyContent={"space-between"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            color={"error"}
            tooltip={t("remove")}
            onClick={() => form.removeExtraParticipant(p)}
            disabled={form.isLocked}
            compact
          />
        </Box>
      ))}

      <Box>
        {!form.isLocked && form.extraParticipantFormModel ? (
          <AddNameAndEmailForm
            form={form.extraParticipantFormModel}
            submit={form.submitExtraParticipant}
            cancel={form.cancelAddExtraParticipant}
          />
        ) : (
          <LinkButton
            label={t("documents.boardMeeting.whoAttendedExtraParticipants.addExternal")}
            icon={"plus"}
            onClick={form.addExtraParticipant}
            disabled={form.isLocked}
          />
        )}
      </Box>
    </VBox>
  );
});

const VotedAgainst = observer(
  ({
    voting,
    form,
    decision,
  }: {
    voting?: VotingModel;
    form: BoardMeetingMinutesForm;
    decision: DecisionItem;
  }) => {
    const { t } = useTranslation();
    if (!voting) {
      return null;
    }

    const unapprovedOutcome = form.unapprovedVotingOutcomeForDecision(
      decision.decisionId,
      form.votingListUsingReview(decision),
    );

    return (
      <>
        <div>{t("documents.voting.votingHelperTitle")}</div>
        {!voting.hasParticipatingMembers ? (
          <Alert
            type={"warning"}
            message={t("documents.boardMeeting.noParticipatingBoardMembersSelected")}
          />
        ) : (
          <>
            <MCheckboxGroup
              model={voting.votedAgainst}
              onChange={(_, member) => form.toggleVotedAgainst(member, voting.decisionId)}
              disabled={form.isLocked}
            />
            {unapprovedOutcome && (
              <Box>
                <Alert
                  type="warning"
                  message={
                    t("dbot.boardmeeting.votingOutcome.notApproved") + ": " + t(unapprovedOutcome)
                  }
                />
              </Box>
            )}
          </>
        )}
      </>
    );
  },
);

const VotedByReview = observer(
  ({
    voting,
    form,
    decision,
  }: {
    voting?: VotingModel;
    form: BoardMeetingMinutesForm;
    decision: DecisionItem;
  }) => {
    if (!voting) {
      return null;
    }
    const decisionVoters = partition(
      form.votingListUsingReview(decision),
      (v) => v.vote === undefined,
    );
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <VBox>
        {decisionVoters[0].map((v) => (
          <Box key={v.companyroleId} alignItems={"center"}>
            <Icon name={"clock"} />
            {t("not-voted", { name: v.name })}
          </Box>
        ))}
        {decisionVoters[1]
          .sort((a) => (a.vote === "for" ? 1 : -1))
          .map((v) => (
            <Box key={v.companyroleId} alignItems={"center"}>
              <Icon name={v.vote === "for" ? "thumbs-up" : "thumbs-down"} />
              {t(v.vote === "for" ? "voted-for" : "voted-against", { name: v.name })}{" "}
              <div className={"subtle"}>{v.votedAt ? t(relativeTime(v.votedAt, locale)) : ""}</div>
            </Box>
          ))}
      </VBox>
    );
  },
);

const Voting = observer(function Voting({
  form,
  decision,
}: {
  form: BoardMeetingMinutesForm;
  decision: DecisionItem;
}) {
  const { t } = useTranslation();
  const voting = form.votesByDecision.get(decision.decisionId);
  const isDecisionMatter = form.isDecisionMatter(decision.decisionId);

  if (!isDecisionMatter) {
    return null;
  }

  return (
    <>
      <strong>{t("dbot.decision")}</strong>
      <MRadioButtonGroup
        model={form.votingModeModelForDecision(decision.decisionId)}
        disabled={form.isLocked}
      />
      {form.isVotedAgainst(decision) && (
        <VotedAgainst voting={voting} form={form} decision={decision} />
      )}
      {form.isVotedUsingReview(decision) && (
        <VotedByReview voting={voting} form={form} decision={decision} />
      )}
    </>
  );
});

export const BoardMeetingAgendaItemDetails = observer(function BoardMeetingAgendaItemDetails({
  form,
  decisionId,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  form: BoardMeetingMinutesForm;
  decisionId: string;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
  if (!decision) {
    return null;
  }

  const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
  return (
    <VBox gap={"md"}>
      {renderDecisionForm(decisionId)}
      {decision.type !== DecisionType.freetext && editor && (
        <DecisionTextsView form={editor} isLocked={form.isLocked} />
      )}
      {renderDecisionAttachment(decisionId)}
      <Voting form={form} decision={decision} />
    </VBox>
  );
});

const DecisionsForIncompetent: FC<{
  form: BoardMeetingMinutesForm;
  memberId: number;
}> = observer(({ form, memberId }) => {
  const { t } = useTranslation();

  return (
    <>
      {form.currentDecisions.map((decision) => {
        return (
          <Box key={decision.decisionId} marginLeft={"lg"}>
            <Checkbox
              label={t(decision.title)}
              onChange={(event) =>
                form.toggleDecisionForIncompetentMember(
                  memberId,
                  decision.decisionId,
                  event.target.checked,
                )
              }
              checked={form.isIncompetentForDecision(memberId, decision.decisionId)}
              disabled={form.isLocked}
            />
          </Box>
        );
      })}
    </>
  );
});

export const BoardMeetingSignatories = observer(function BoardMeetingSigners({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"xl"}>
      {form.boardMembers.length === 1 && (
        <Box>
          {t("documents.boardMeeting.signatories.info.singleBoardMember", {
            companyName: form.context.initialState.company.name,
          })}
        </Box>
      )}
      {form.boardMembers.length < 5 && form.boardMembers.length > 1 && (
        <Box>
          {t("documents.boardMeeting.signatories.info.fewerThanFiveBoardMembers", {
            companyName: form.context.initialState.company.name,
          })}
        </Box>
      )}
      {supportsSelectionOfSigners(form.board) && (
        <VBox gap={"md"}>
          <RadioFieldQuestion
            question={{ key: "documents.boardMeeting.whoWillSign" }}
            fieldName={"physicalMeetingSignersMode"}
            field={form.physicalMeetingSignersMode}
            onChange={form.setPhysicalMeetingSignersMode}
            isLocked={form.isLocked || !form.isSelectionOfSignersEnabled}
            infoText={{ key: "documents.boardMeeting.deadlineCommentsDate-helperText" }}
          />
          {!form.isSelectionOfSignersEnabled && (
            <Alert type={"info"} message={t("dbot.boardmeeting.all-must-sign-when-circulation")} />
          )}
        </VBox>
      )}
      <VBox>
        <Box fontWeight={"bold"}>
          {t("documents.boardMeeting.signatories.participatingBoardMembers")}
        </Box>
        <MCheckboxGroup
          model={form.selectedSigningBoardMembers}
          disabled={form.isLocked || form.physicalMeetingSignersMode.value === SignersMode.ALL}
        />
      </VBox>
      {form.showOptionalSignatories && (
        <VBox>
          <Box fontWeight={"bold"}>
            {t("documents.boardMeeting.signatories.additionalSignatories")}
          </Box>
          <CollapsibleCheckboxGroup
            model={form.selectedSigningNotParticipatingBoardMembers}
            bulkSelectLabel={{
              key: "dbot.boardmeeting.selectNotParticipatinBoardMember",
              params: { n: form.selectedSigningNotParticipatingBoardMembers.options.length },
            }}
            disabled={form.isLocked}
            labelFn={(item) =>
              t("dbot.boardmeeting.did-not-participate", {
                name: getCompanyRoleLabelById(item.companyroleId, form.roles),
              })
            }
          />
          <CollapsibleCheckboxGroup
            model={form.selectedSigningCeos}
            bulkSelectLabel={{
              key: "dbot.boardmeeting.selectCeos",
              params: { n: form.selectedSigningCeos.options.length },
            }}
            disabled={form.isLocked}
            labelFn={(item) => t(getCompanyRoleLabelById(item.companyroleId, form.roles))}
          />
          <CollapsibleCheckboxGroup
            model={form.selectedSigningObservers}
            bulkSelectLabel={{
              key: "dbot.boardmeeting.selectObservers",
              params: { n: form.selectedSigningObservers.options.length },
            }}
            disabled={form.isLocked}
            labelFn={(item) => t(getCompanyRoleLabelById(item.companyroleId, form.roles))}
          />
          <CollapsibleCheckboxGroup
            model={form.extraSignatories}
            bulkSelectLabel={{
              key: "dbot.boardmeeting.selectExtraParticipant",
              params: { n: form.extraSignatories.options.length },
            }}
            disabled={form.isLocked}
            labelFn={(item) => t("dbot.boardmeeting.extraParticipantLabel", { name: item.name })}
          />
        </VBox>
      )}
      {form.physicalMeetingSignersMode.value === SignersMode.SELECTED && (
        <>
          <MDateInput
            label={t("documents.boardMeeting.whenIsDeadlineForComments")}
            model={form.deadlineForNotes}
          />
          <Caption>{t("documents.boardMeeting.deadlineCommentsDate-helperText")}</Caption>
        </>
      )}
    </VBox>
  );
});

export const BoardMeetingChair = observer(function BoardMeetingChair({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox>
        <SelectField
          field={form.meetingChair}
          name={"meetingChair"}
          onChange={form.setMeetingChair}
          isLocked={form.isLocked || !form.canSelectMeetingChair}
        />
      </VBox>
      {form.cannotSelectMeetingChairMessage && (
        <Alert type={"info"} message={t(form.cannotSelectMeetingChairMessage)} />
      )}
    </VBox>
  );
});

export const BoardMeetingParticipants = observer(function BoardMeetingParticipants({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <VBox>
        <div>{t("documents.boardMeeting.whoAttended")}</div>
        <CollapsibleCheckboxGroup
          model={form.selectedBoardMembers}
          bulkSelectLabel={{
            key: "dbot.boardmeeting.selectBoardMembers",
            params: { n: form.selectedBoardMembers.options.length },
          }}
          disabled={form.isLocked}
          initiallyExpanded={form.selectedBoardMembers.selected.length !== form.boardMembers.length}
          onToggleAll={(checked, toggleCollapse, isCollapsed) => {
            form.toggleAllBoardMembers(checked);
            if (isCollapsed && !checked) toggleCollapse();
          }}
          onChange={(_event, option) => form.toggleSelectedBoardMember(option)}
        />
        <CollapsibleCheckboxGroup
          model={form.selectedCeos}
          bulkSelectLabel={{
            key: "dbot.boardmeeting.selectCeos",
            params: { n: form.selectedCeos.options.length },
          }}
          disabled={form.isLocked}
          labelFn={(item) => t(getCompanyRoleLabelById(item.companyroleId, form.roles))}
        />
        <CollapsibleCheckboxGroup
          model={form.selectedDeputyMembers}
          bulkSelectLabel={{
            key: "dbot.boardmeeting.selectDeputyMembers",
            params: { n: form.selectedDeputyMembers.options.length },
          }}
          disabled={form.isLocked}
          labelFn={(item) =>
            t("dbot.boardmeeting.deputyBoardMemberOptionLabel", { name: item.name })
          }
          initiallyExpanded={
            form.selectedDeputyMembers.selected.length !== 0 &&
            form.selectedDeputyMembers.selected.length !== form.deputyBoardMembers.length
          }
          onToggleAll={(checked) => form.toggleAllDeputyBoardMembers(checked)}
          onChange={(_event, option) => form.toggleSelectedDeputyBoardMember(option)}
        />
        <CollapsibleCheckboxGroup
          model={form.selectedObservers}
          bulkSelectLabel={{
            key: "dbot.boardmeeting.selectObservers",
            params: { n: form.selectedObservers.options.length },
          }}
          disabled={form.isLocked}
          labelFn={(item) => t("dbot.boardmeeting.observerOptionLabel", { name: item.name })}
          initiallyExpanded={
            !form.selectedObservers.isAllSelected && !form.selectedObservers.isNoneSelected
          }
        />
      </VBox>
      <ExtraParticipants form={form} />
      <VBox>
        <Box>
          <div>{t("documents.boardMeeting.incompetentBoardMembers")}</div>
        </Box>
        <CollapseList closeLabel={t("collapse")} openLabel={t("dbot.showAll")}>
          <VBox>
            {form.presentBoardMembers.map((member) => (
              <VBox key={`${member.name}_member`}>
                <Checkbox
                  label={member.name}
                  onChange={(event) =>
                    form.toggleIncompetentBoardMember(member.companyroleId, event.target.checked)
                  }
                  checked={form.isIncompetent(member.companyroleId)}
                  disabled={form.isLocked}
                />
                {form.isIncompetent(member.companyroleId) && (
                  <VBox>
                    <DecisionsForIncompetent form={form} memberId={member.companyroleId} />
                  </VBox>
                )}
              </VBox>
            ))}
          </VBox>
        </CollapseList>
      </VBox>
      <LocalizedTextEditor model={form.additionalParticipantDetailsTextModel} />
    </VBox>
  );
});

export const BoardMeetingDetails = observer(function BoardMeetingDetails({
  form,
}: {
  form: BoardMeetingMinutesForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <MTextInput
        name={"boardMeetingNumber"}
        label={t("documents.noticeBoardMeeting.boardMeetingNumberHelperText")}
        prefix={"#"}
        model={form.boardMeetingNumber}
        readOnly={form.isLocked}
      />
      <VBox>
        <RadioFieldQuestion
          question={{ key: "documents.meetingform" }}
          field={form.boardMeetingForm}
          fieldName={"boardMeetingForm"}
          row
          isLocked={form.isLocked}
        />
      </VBox>
      {form.boardMeetingForm.value === BoardMeetingForm.CIRCULATION ? (
        <VBox>
          <Box>
            <div>{t("documents.boardMeeting.date-circulation")}</div>
          </Box>
          <LocalDateTimePicker
            dateLabel={{ key: "date" }}
            dateField={form.meetingStartDate}
            timeLabel={{ key: "time" }}
            timeField={form.meetingStartTime}
            isLocked={form.isLocked}
          />
        </VBox>
      ) : (
        <VBox>
          <Box>
            <div>{t("documents.boardMeeting.dateBoardMeetingQuestion")}</div>
          </Box>
          <LocalDateTimePicker
            dateLabel={{ key: "date" }}
            dateField={form.meetingStartDate}
            timeLabel={{ key: "documents.boardMeeting.dateBoardMeetingStartTimeLabel" }}
            timeField={form.meetingStartTime}
            endTimeLabel={{
              key: "documents.boardMeeting.dateBoardMeetingEndTimeLabel",
            }}
            endTimeField={form.meetingEndTime}
            isLocked={form.isLocked}
          />
        </VBox>
      )}
      {form.showAddress && (
        <RadioFieldQuestion
          field={form.meetingPlace}
          fieldName={"meetingPlace"}
          question={{ key: "documents.noticeBoardMeeting.noticeToBoardMeetingPlace" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showInlineAddress && (
        <TextInput
          label={t("documents.meetingForm.address")}
          value={form.inlineAddress.inputValue}
          onChange={form.inlineAddress.setInputValue}
          onBlur={form.inlineAddress.commit}
          readOnly={form.isLocked}
        />
      )}
      <LocalizedTextEditor model={form.additionalDetailsTextModel} />
    </VBox>
  );
});

export const BoardMeetingMinutesDetailsPanel = observer(function BoardMeetingMinutesDetailsPanel({
  panel,
  form,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  panel: DetailsPanel;
  form: BoardMeetingMinutesForm;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  switch (panel.type) {
    case "decision":
      return (
        <BoardMeetingAgendaItemDetails
          form={form}
          decisionId={panel.id}
          renderDecisionForm={renderDecisionForm}
          renderDecisionAttachment={renderDecisionAttachment}
        />
      );
    case "custom":
      switch (panel.id) {
        case BoardMinutesCustomPanel.meetingDetails:
          return <BoardMeetingDetails form={form} />;
        case BoardMinutesCustomPanel.participants:
          return <BoardMeetingParticipants form={form} />;
        case BoardMinutesCustomPanel.meetingChair:
          return <BoardMeetingChair form={form} />;
        case BoardMinutesCustomPanel.signatories:
          return <BoardMeetingSignatories form={form} />;
        case CommonBoardMeetingCustomPanel.noticeToGm:
          return <NoticeToGmPanel />;
      }
  }
  return null;
});

export const BoardMeetingMinutesView2 = observer(({ form }: { form: BoardMeetingMinutesForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <VBox gap="xs">
        <DocumentSigningStatusLine context={form.context} dispatch={form.dispatch} />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getCustomBoardMeetingPanels(form)}
        />
      </VBox>
      <VBox gap={"xs"}>
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getDecisionDetailsPanels(form)}
        />
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.isLocked}
          panels={getNoticeToGmPanels(form.context)}
        />
      </VBox>
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={prepUploadDocumentAttachmentsButton(form.context, form.dispatch)}
      />
      {when(prepDocumentAttachmentsPanel(form.context, form.dispatch), (props) => (
        <AttachmentsPanel {...props} />
      ))}
      {form.isNorwegianRequiredForMinutesWithPoA ? (
        <VBox textAlign={"center"} alignItems={"center"} marginBottom={"lg"}>
          <Icon name={"lightbulb"} />
          <Markdown
            value={t("dbot.share-capital-increase.norwegian-language-requirement-info")}
            enableLinks
            onActionClick={(name) => {
              switch (name) {
                case "#select-language-no-en":
                  form.documentLanguage.setValue("no/en");
                  break;
                case "#select-language-no":
                  form.documentLanguage.setValue("no");
                  break;
              }
            }}
          />
        </VBox>
      ) : (
        when(form.openSendForSigningDialogAction, (sendForSigningAction) => (
          <Button
            fullWidth
            variant={"primary"}
            label={t(sendForSigningAction.label)}
            loading={isPending(form.context.documentSigners.get(form.documentId))}
            onClick={(e) => {
              if (e.shiftKey && !isProductionEnvironment()) {
                form.dispatch({
                  type: "dbot/autosign-document",
                  document: form.context.currentDocuments.find(
                    (d) => d.documentId === form.documentId,
                  )!,
                  label: { translated: "Autosign document" },
                });
              } else {
                form.dispatch(sendForSigningAction);
              }
            }}
            splitActions={
              form.sendForReviewAction
                ? [
                    {
                      type: "item",
                      icon: "pen",
                      label: sendForSigningAction.label,
                      onSelect: () => form.dispatch(sendForSigningAction),
                    },
                    {
                      type: "item",
                      icon: "paper-plane",
                      label: form.sendForReviewAction.label,
                      onSelect: () => form.dispatch(form.sendForReviewAction!),
                    },
                  ]
                : undefined
            }
          />
        ))
      )}

      {when(form.downloadSignedDocumentAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      <ReopenDocumentButton context={form.context} dispatch={form.dispatch} />
    </VBox>
  );
});
